import CloseIcon from "@mui/icons-material/Close";
import { Box, Divider, Grid } from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import OverlaySpinner from "../../../../common/components/functional/overlaySpinner/OverlaySpinner";
import { EditEntry, Entry, PostEditEntry, Supplement } from "../../entriesAPI";
import {
  addNewEntry,
  editEntry,
  fetchEntriesByWrkentAddIds,
  fetchEntryById,
  postDeleteEntry,
} from "../../entriesSlice";
import Top from "../Top";
import BasicInfo from "./BasicInfo";
import Comments from "./Comments/Comments";
import EntrySupplements from "./EntrySupplements/EntrySupplements";
import PayrollList from "./PayrollList/PayrollList";

import { isFulfilled } from "@reduxjs/toolkit";
import { useNavigate, useParams } from "react-router-dom";
import { areObjectsEqual } from "../../../../app/common";
import { NO_DRAG_SELECTOR } from "../../../../app/constants";
import { ConfirmationDialog } from "../../../../common/components/functional/confirmationDialog/ConfirmationDialog";
import { CustomButton } from "../../../../common/components/functional/customButton/CustomButton";
import useMessage from "../../../../hooks/useMessage";
import { canExpertEditEntries } from "../../../user/accessRightChecks";
import { AddEntry } from "../../types/commands";
import EntryChanges from "../EntryChanges";
import EntryCollapse from "../Shared/EntryCollapse";
import { shouldEnforceNegativeValue } from "./helpers";
import {
  supplementInputHasError,
  supplementsWithAllowedInputs,
  supplementWithAllowedInputs,
} from "./supplementInputTypes";

type Props = {
  selectedEntry?: Entry | EditEntry | null;
  cancel: () => void;
  setConvertModalOpen?: (open: boolean) => void;
  setCopyModalOpen?: (entry: Entry) => void;
};

const EditEntryView = ({
  selectedEntry,
  cancel,
  setConvertModalOpen,
  setCopyModalOpen,
}: Props) => {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const [entry, setEntry] = useState<EditEntry | null>(null);
  const [originalEntry, setOriginalEntry] = useState<EditEntry | null>(null);
  const [inputErrors, setInputErrors] = useState<{ [code: string]: boolean }>(
    {},
  );

  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    markSelectedAsApproved: boolean;
  }>({ isOpen: false, markSelectedAsApproved: false });

  const [deleteDialogIsOpen, setDeleteDialogIsOpen] = useState<boolean>(false);

  const { talentStatus, entriesStatus, assignmentSupplements } = useAppSelector(
    (state: any) => ({
      talentStatus: state.talents.status,
      entriesStatus: state.entries.entries.status,
      assignmentSupplements: state.talents.assignment?.supplements,
    }),
  );

  const { sendMessage } = useMessage();

  const params = useParams();
  const navigate = useNavigate();

  const isLoading = talentStatus === "pending" || entriesStatus === "pending";

  useEffect(() => {
    return () => {
      if (params?.entryId) {
        navigate(`/home/talents/${params.talentId}/entries`, { replace: true });
      }
    };
  }, [navigate, params?.entryId, params.talentId]);

  useEffect(() => {
    if (!selectedEntry?.id && !params.entryId) {
      // new entry created
      setOriginalEntry(selectedEntry as EditEntry);
      return setEntry(selectedEntry as EditEntry);
    }

    if (selectedEntry?.id || params?.entryId) {
      // !!selectedEntryId = entry selected from the entries list
      // !selectedEntryId && params.entryId = newly created entry opened by clicking the snackbar
      const id = selectedEntry?.id || (params?.entryId as string);
      dispatch(fetchEntryById(id)).then((action) => {
        if (action.meta.requestStatus === "fulfilled") {
          const responseData = action.payload as EditEntry;
          const entryData = { ...responseData };
          setEntry({ ...entryData });
          setOriginalEntry({ ...entryData });
        } else {
          console.error("Error in fetching entry by id");
          // TODO: Handle error?
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, selectedEntry?.id]);

  const handleCancel = () => {
    // Clear states
    setEntry(null);
    setOriginalEntry(null);
    // Call cancel, which will also clean selectedEntryId
    cancel();
  };

  const handleSave = (markSelectedAsApproved?: boolean) => {
    // Explicitly check if markSelectedAsApproved is true
    if (markSelectedAsApproved === true) {
      // Ask confirmation from user about approval. Also closes the modal in the end
      setConfirmDialog({ isOpen: true, markSelectedAsApproved });
    } else {
      // Treats undefined as false and calls handleConfirmSave directly, without confirmation dialog
      handleConfirmSave(false);
    }
  };

  const handleConfirmSave = (approve?: boolean) => {
    const hasErrors = Object.values(inputErrors).includes(true);
    console.error("Errors :", inputErrors);

    const isNew = !entry?.id;

    if (isNew && entry) {
      const entryDetails: AddEntry = {
        assignment: entry.assignmentId,
        isBillable: entry.isBillable,
        markApproved: approve || false,
        details: {
          type: "WORK_ENTRY",
          start: entry.start,
          end: entry.end,
          customerCostCenterId: entry.customerCostCenterId,
          supplements: supplementsWithAllowedInputs(
            entry.supplements,
            assignmentSupplements,
          ),
          lunchBreak:
            entry.lunchBreak?.start && entry.lunchBreak?.duration
              ? {
                  start: entry.lunchBreak?.start,
                  duration: entry.lunchBreak?.duration,
                }
              : null,
          maxHoursToBank: entry.maxHoursToBank,
          flexibleHours: entry.flexibleHours,
        },
      };

      return dispatch(addNewEntry(entryDetails)).then((action) => {
        if (isFulfilled(action)) {
          const event = action.payload[0];

          sendMessage(t("hourEntries:workTimeEntryAdded"), "success", {
            link: `/home/talents/${params.talentId}/entries/${event.id}`,
          });

          /* Backend isn't able to provide enough data in wrkentAdd event,
                                     so we need to fetch the entry separately to update entries list.
                                     Look through events, if there's a wrkEntAdd event */
          const workEntryAddEvents = action.payload
            .filter((event: any) => event?.name === "wrkentAdd")
            .map((event: any) => ({
              ...event,
              payload: {
                ...event.payload,
                splitFrom: event.payload.splitFrom ?? "", // Ensure splitFrom exists, eventhought new entry doesn't have it
              },
            }));

          if (workEntryAddEvents.length > 0) {
            dispatch(fetchEntriesByWrkentAddIds(workEntryAddEvents));
          }

          handleCancel();
        }
      });
    }

    if (
      !hasErrors &&
      entry &&
      entry.start &&
      entry.end &&
      (!areObjectsEqual(entry, originalEntry) || approve === true)
    ) {
      const entryDetails: PostEditEntry = {
        id: entry.id,
        version: entry.version,
        type: entry.type,
        start: entry.start,
        end: entry.end,
        isBillable: entry.isBillable,
        customerCostCenterId: entry.customerCostCenterId,
        comment: entry.comment,
        supplements: supplementsWithAllowedInputs(
          entry.supplements,
          assignmentSupplements,
        ),
        lunchBreak:
          entry?.lunchBreak !== null && entry?.lunchBreak !== undefined
            ? {
                start: entry.lunchBreak.start,
                duration: entry.lunchBreak.duration,
              }
            : null,
        markApproved: approve || false, // Consider undefined approve as false
        maxHoursToBank: entry.maxHoursToBank,
        flexibleHours: entry.flexibleHours,
      };

      dispatch(editEntry(entryDetails)).then((action: any) => {
        if (action.meta.requestStatus === "fulfilled") {
          handleCancel();
        }
      });
    } else {
      // handleCancel();
    }
  };

  // Validate the supplements and generate input error flags
  const validateSupplements = (newSupplements: Supplement[]) => {
    // Object to track input errors for each supplement code
    const inputErrors: Record<string, boolean> = {};

    // Map through each supplement and validate/update as needed
    const validatedSupplements = newSupplements.map((supplement) => {
      const { code, amount, time } = supplement;
      const definition = assignmentSupplements?.[code]?.definition;

      if (definition?.type === "TIMERANGE") {
        const updatedSupplement = supplementWithAllowedInputs(
          supplement,
          definition,
        );
        if (
          supplementInputHasError(
            updatedSupplement,
            definition,
            entry!.start,
            entry!.end,
          )
        ) {
          inputErrors[code] = true;
        }
        return updatedSupplement;
      }

      const updatedSupplement: Supplement = { ...supplement };

      // Validate and update 'amount' if defined
      if (amount !== undefined && amount !== null) {
        const enforceNegativeValue = shouldEnforceNegativeValue(
          supplement.code,
        );

        let isAmountValid = false;
        if (enforceNegativeValue) {
          isAmountValid = Number(amount) <= 0;
        } else {
          isAmountValid = Number(amount) > 0;
        }
        updatedSupplement.amount = amount;

        // Set the input error flag if 'amount' is not valid
        if (!isAmountValid) {
          inputErrors[code] = true;
        } else {
          // Delete the key from inputErrors if 'amount' is valid
          delete inputErrors[code];
        }
      }

      // Validate and update 'time' if defined
      if (
        time !== undefined &&
        time !== null &&
        time.start !== undefined &&
        time.end !== undefined
      ) {
        const startEpoch = time.start;
        const endEpoch = time.end;

        // Check if 'startEpoch' is greater than or equal to 'endEpoch' or outside entry's date range
        const isTimeNotValid =
          startEpoch >= endEpoch ||
          startEpoch < entry!.start ||
          endEpoch > entry!.end;
        updatedSupplement.time = time;

        // Set the input error flag if 'time' is not valid
        if (isTimeNotValid) {
          inputErrors[code] = true;
        } else {
          // Delete the key from inputErrors if 'time' is valid
          delete inputErrors[code];
        }
      }
      return updatedSupplement;
    });

    // Return the validated supplements along with input error flags
    return { validatedSupplements, inputErrors };
  };

  // Updates supplements in entry
  const updateSupplements = (newSupplements: Supplement[]) => {
    const { validatedSupplements, inputErrors } =
      validateSupplements(newSupplements);

    entry &&
      setEntry({
        ...entry,
        supplements: validatedSupplements,
      });

    setInputErrors({ ...inputErrors });
  };

  const handleDeleteEntry = () => {
    if (entry?.id && entry?.version) {
      dispatch(postDeleteEntry({ id: entry.id, version: entry.version })).then(
        (action: any) => {
          if (action.meta.requestStatus === "fulfilled") {
            sendMessage(t("hourEntries:workTimeEntryDeleted"), "success");
            handleCancel();
          }
        },
      );
    }
  };

  // Go through approvalStateChanges and show the lastest change that has comment (customer's feedback)
  const latestCustomerStatusChange = entry?.approvalStateChanges
    ?.filter((change) => change?.comment) // Filter out changes without a comment or with an empty string as the comment
    .sort((a, b) => b.timestamp - a.timestamp)[0]; // Sort by timestamp in descending order

  const entryHasComments = entry?.feedback || latestCustomerStatusChange;
  // Only show delete button if entry is not new, a shift, or a targeted shift
  const canDeleteEntry =
    !!entry?.id && !entry?.isShift && !entry?.isTargetedShift;

  return (
    entry && (
      <Box
        sx={{
          width: "1229px",
          padding: "0px 15px 15px",
          maxHeight: "95vh",
          overflowY: "auto",
          overflowX: "hidden",
          scrollbarColor: "transparent transparent",
        }}
      >
        <OverlaySpinner isLoading={isLoading} />
        <Top
          entry={entry}
          showEditEntryActions={!!entry.id}
          handleSave={handleSave}
          handleCancel={handleCancel}
          setConvertModalOpen={setConvertModalOpen}
          setCopyModalOpen={setCopyModalOpen}
        />
        <Box
          className={NO_DRAG_SELECTOR}
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              backgroundColor: "#FFFFFF",
              borderRadius: "8px",
              padding: "24px 32px",
              width: entryHasComments ? "70%" : "100%",
            }}
          >
            <Grid
              container
              sx={{
                display: "flex",
              }}
            >
              <BasicInfo
                entry={entry}
                setEntry={setEntry}
                inputErrors={inputErrors}
                setInputErrors={setInputErrors}
              />
              <EntrySupplements
                originalEntry={originalEntry}
                entry={entry}
                updateSupplements={updateSupplements}
                inputErrors={inputErrors}
                setInputErrors={setInputErrors}
              />
            </Grid>
            <Divider
              sx={{
                marginTop: "24px",
                marginBottom: "24px",
              }}
            />
            <PayrollList entry={entry} setEntry={setEntry} />
            <EntryCollapse
              openText={t("hourEntries:openChangelog")}
              closeText={t("hourEntries:closeChangelog")}
            >
              <EntryChanges entry={entry} />
            </EntryCollapse>
          </Box>
          {entryHasComments && (
            <Box
              sx={{
                flexDirection: "column",
                width: "30%",
              }}
            >
              <Comments entry={entry} />
              {/* add JobSatisfaction.tsx */}
            </Box>
          )}
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            cursor: "grab",
          }}
        >
          <Box>
            {canDeleteEntry && (
              <CustomButton
                startIcon={<CloseIcon />}
                variant="outlined"
                accessRightChecks={[canExpertEditEntries]}
                onClick={() => setDeleteDialogIsOpen(true)}
                sx={{
                  color: "#FF0000",
                  borderColor: "#FF0000",
                  minHeight: "42px",
                  borderRadius: "20px",
                  minWidth: "120px",
                  marginTop: "35px",
                }}
              >
                {t("hourEntries:deleteEntry")}
              </CustomButton>
            )}
          </Box>
          <Box>
            <CustomButton
              accessRightChecks={[canExpertEditEntries]}
              disabled={
                Object.values(inputErrors).includes(true) ||
                areObjectsEqual(entry, originalEntry) ||
                !entry?.customerCostCenterId ||
                isLoading
              }
              onClick={() => handleSave()}
              variant={"outlined"}
              sx={{
                minHeight: "42px",
                borderRadius: "20px",
                minWidth: "120px",
                marginTop: "35px",
              }}
            >
              {t("common:save")}
            </CustomButton>
            {entry?.approvalState !== "Approved" && (
              <CustomButton
                accessRightChecks={[canExpertEditEntries]}
                disabled={
                  !!Object.values(inputErrors).includes(true) ||
                  !entry.customerCostCenterId ||
                  isLoading
                }
                onClick={() => handleSave(true)}
                variant={"contained"}
                sx={{
                  minHeight: "42px",
                  borderRadius: "20px",
                  minWidth: "120px",
                  marginTop: "35px",
                  marginLeft: "10px",
                }}
              >
                {t("hourEntries:saveAndApprove")}
              </CustomButton>
            )}
          </Box>
          <ConfirmationDialog
            title={t("hourEntries:entryApprovalConfirmationTitle")}
            contentText={t("hourEntries:entryApprovalConfirmationContext")}
            confirmText={t("hourEntries:entriesConfirmButton")}
            cancelText={t("hourEntries:entriesCancelButton")}
            isOpen={confirmDialog.isOpen}
            onCancel={() =>
              setConfirmDialog({ isOpen: false, markSelectedAsApproved: false })
            }
            onConfirm={() => handleConfirmSave(true)} // Mark for approve, as it's only used for approval confirmation
          />
          <ConfirmationDialog
            title={t("hourEntries:entryDeleteConfirmationTitle")}
            contentText={t("hourEntries:entryDeleteConfirmationContext")}
            confirmText={t("hourEntries:entriesConfirmButton")}
            cancelText={t("hourEntries:entriesCancelButton")}
            isOpen={deleteDialogIsOpen}
            onCancel={() => setDeleteDialogIsOpen(false)}
            onConfirm={() => handleDeleteEntry()}
          />
        </Box>
      </Box>
    )
  );
};

export default EditEntryView;
